import { Component, OnInit, AfterViewInit, ViewChildren, ViewChild, ElementRef, QueryList } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { UntypedFormBuilder, Validators, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { AnyRequiredValidator } from '../shared/any-required.validator';
import { DependantRequiredValidator } from '../shared/dependant-required.validator';
import { ExclusiveValidator } from '../shared/exclusive.validator';
import { DNASampleService } from '../../services/dna-sample.service';
import { IDNASampleSearchResult } from "../../interfaces/dna-sample-search-result";
import { NgbdSortableHeader, SortEvent, Compare } from "../../directives/ngbd-sortable-header.directive";
import { environment } from '../../../environments/environment';
import { UserService } from '../../services/user.service';
import { IUser } from '../../interfaces/user';
import { Permission } from '../../interfaces/permission.enum';
import { ConfigurationService } from '../../services/configuration.service';
import { ErrorService } from '../../services/error.service';
import { IExternalLinks } from '../../interfaces/external-links';

@Component({
  selector: 'app-sample-search',
  templateUrl: './sample-search.component.html',
  styles: [],
  standalone: false
})
export class SampleSearchComponent implements OnInit, AfterViewInit {
  @ViewChild("needFocus") lastNameField: ElementRef = {} as ElementRef;
  @ViewChild("needFocusCORIS") vaCorisNumberField: ElementRef = {} as ElementRef;
  @ViewChild("sidTextbox") sidTextbox: ElementRef = {} as ElementRef;
  @ViewChildren(NgbdSortableHeader) sortableHeaders: QueryList<NgbdSortableHeader>;

  public searchForm: UntypedFormGroup;

  public working: boolean = false;
  public searchResults: IDNASampleSearchResult[];
  public sortedSearchResults: IDNASampleSearchResult[];
  public errorMessage: string;
  public resultCountLimit: number;
  public showVACORISNumber: boolean = false;
  public showTooManyResultsMessage: boolean;
  public externalLinks: IExternalLinks;

  public searchTipsVACORISCollapsed: boolean = true;
  public searchTipsNameCollapsed: boolean = true;
  public searchTipsSSNSIDCollapsed: boolean = true;
  public searchTipsNameSSNSIDCollapsed: boolean = true;

  public get lastName() {
    return this.searchForm.get("lastName");
  }

  public get firstName() {
    return this.searchForm.get("firstName");
  }

  public get ssn() {
    return this.searchForm.get("ssn");
  }

  public get sid() {
    return this.searchForm.get("sid");
  }

  public get docNumber() {
    return this.searchForm.get("docNumber");
  }

  public constructor(
    private readonly titleService: Title,
    private readonly fb: UntypedFormBuilder,
    private readonly dnaSampleService: DNASampleService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly configService: ConfigurationService,
    private readonly errorService: ErrorService
  ) { }

  public ngOnInit(): void {
    this.titleService.setTitle("DNA Data Bank Sample Search");

    this.resultCountLimit = environment.dnaSearchResultCountLimit;

    let currentUser: IUser = this.userService.getCurrentUser();
    this.showVACORISNumber = currentUser.permissions.includes(Permission.ViewVACORISNumber);

    this.route.paramMap.subscribe(params => {
      let paramLastName = params.get("lastName");
      if (paramLastName) paramLastName = paramLastName.toUpperCase();
      let paramFirstName = params.get("firstName");
      if (paramFirstName) paramFirstName = paramFirstName.toUpperCase();
      let paramSsn = params.get("ssn");
      let paramSid = params.get("sid");
      let paramDocNumber = params.get("vacoris");

      this.searchForm = this.fb.group({
        lastName: [paramLastName, [Validators.minLength(2)]],
        firstName: [paramFirstName, [Validators.minLength(2)]],
        ssn: [paramSsn],
        sid: [paramSid ? paramSid : "VA"],
        docNumber: [paramDocNumber]
      }, {
        validators: this.showVACORISNumber ?
          [AnyRequiredValidator(["lastName", "ssn", "sid", "docNumber"]), DependantRequiredValidator("lastName", "firstName"), ExclusiveValidator("docNumber", ["lastName", "ssn", "sid"], "VA")] :
          [AnyRequiredValidator(["lastName", "ssn", "sid"]), DependantRequiredValidator("lastName", "firstName")]});

      if (paramLastName || paramFirstName || paramSsn || paramSid || (this.showVACORISNumber && paramDocNumber)) {
        this.searchForm.markAllAsTouched();
        this.searchForm.updateValueAndValidity();
        if (this.searchForm.valid) {
          this.doSearch();
        }
      } else {
        this.searchResults = undefined;
      }
    });

    this.configService.getExternalLinks()
      .subscribe(
        data => this.externalLinks = data,
        error => this.errorMessage = this.errorService.getErrorMessage(error)
      );
  }

  public ngAfterViewInit(): void {
    if (this.showVACORISNumber)
      this.vaCorisNumberField.nativeElement.focus();
    else
      this.lastNameField.nativeElement.focus();
  }

  // Manage SID default value (place cursor after "VA" when tabbing in if only "VA" is there)
  public ssnKeyDown(e: KeyboardEvent): void {
    e.preventDefault();

    if (this.sid.value === "VA") {
      this.setInputSelection(this.sidTextbox.nativeElement, 2, 2);
    } else {
      this.setInputSelection(this.sidTextbox.nativeElement, 0, this.sid.value ? this.sid.value.length : 0);
    }
  }

  private setInputSelection(input: any, startPos: number, endPos: number): void {
    if (input.setSelectionRange) {
      input.focus();
      input.setSelectionRange(startPos, endPos);
    } else if (input.createTextRange) {
      var range = input.createTextRange();
      range.collapse(true);
      range.moveEnd('character', endPos);
      range.moveStart('character', startPos);
      range.select();
    }
  }

  public clear(): void {
    this.lastName.setValue("");
    this.firstName.setValue("");
    this.ssn.setValue("");
    this.sid.setValue("VA");
    this.docNumber.setValue("");

    this.router.navigate(["./", {}], { relativeTo: this.route });
  }

  public onSubmit(): void {
    let params: { [k: string]: string } = {};
    if (this.lastName.value) params.lastName = this.lastName.value;
    if (this.firstName.value) params.firstName = this.firstName.value;
    if (this.ssn.value) params.ssn = this.ssn.value;
    if (this.sid.value && this.sid.value !== "VA") params.sid = this.sid.value;
    if (this.showVACORISNumber && this.docNumber.value) params.vacoris = this.docNumber.value;

    this.router.navigate([ "./", params ], { relativeTo: this.route });
  }

  public doSearch(): void {
    this.working = true;
    let sid = this.sid.value && this.sid.value !== "VA" ? this.sid.value : undefined;
    this.dnaSampleService.searchDNASamples(this.lastName.value, this.firstName.value, this.ssn.value, sid, this.docNumber.value, this.showVACORISNumber)
      .subscribe(data => {
        // The query will return environment.resultCountLimit + 1 records so we will know there are too many, so remove the "+1" record
        if (data.length > environment.dnaSearchResultCountLimit) {
          data.pop();
          this.showTooManyResultsMessage = true;
        } else {
          this.showTooManyResultsMessage = false;
        }
        this.searchResults = data;
        // For some reason, this doesn't add the class to the header the first time,
        // so the direction attribute will also need to be added to the header to cover that scenario
        this.onSort({ column: "dateCollected", direction: "desc" });
        this.working = false;
      }, error => {
        this.errorMessage = this.errorService.getErrorMessage(error);
          this.working = false;
      });
  }

  public renderSSNs(ssns: string[]) {
    if (!ssns)
      return "";

    return ssns.join("<br />");
  }

  public onSort({ column, direction }: SortEvent) {

    // resetting other headers
    this.sortableHeaders.forEach(header => {
      if (header.sortable === column) {
        header.direction = direction;
      } else {
        header.direction = '';
      }
    });

    // sorting
    this.searchResults.sort((a: any, b: any) => {
      const res = Compare(a[column], b[column]);
      return direction === 'asc' ? res : -res;
    });
  }

  public onDetailsClick(sampleId: number) {
    this.dnaSampleService.getDNASampleTempId(sampleId)
      .subscribe(
        data => {
          this.router.navigate(["sample", data.tempSampleId]);
        },
        error => this.errorMessage = this.errorService.getErrorMessage(error)
      );
  }
}
